import { FC, useCallback } from 'react';

import styled from '@emotion/styled';
import MuiButton, { buttonClasses } from '@mui/material/Button';
import { useTranslations } from 'next-intl';
import { useCopyToClipboard } from 'usehooks-ts';
import type { Address } from 'viem';

import { showSnackbar } from '@web/ui/components';
import { getBorder, getStateLayerColor } from '@web/ui/theme/utils';
import { sliceWalletAddress } from '@web/utils/wallet';

const Container = styled.div<{ fullWidth?: boolean }>`
  display: flex;
  gap: ${({ theme }) => theme.spacing('0500')};
  align-items: center;
  ${({ fullWidth }) => fullWidth && `justify-content: space-between;`}
`;

const WalletAddress = styled.span`
  font: ${({ theme }) => theme.typography.body.regular};
  font-size: ${({ theme }) => theme.typescale.size['1']};
  color: ${({ theme }) => theme.color.onSurface};
  opacity: ${({ theme }) => theme.opacity.opaque};
  cursor: pointer;

  :hover {
    text-decoration: ${({ theme }) => theme.textDecoration.underline};
  }
`;

const Copy = styled(MuiButton)`
  height: ${({ theme }) => theme.size['1500']};

  &.${buttonClasses.root} {
    font: ${({ theme }) => theme.typography.body.regular};
    font-size: ${({ theme }) => theme.typescale.size['0750']};
    color: ${({ theme }) => theme.color.onSurface};
    border-radius: ${({ theme }) => theme.shape.corner['0250']};
    min-width: 0;
    padding-inline: ${({ theme }) => theme.spacing('0500')};
    ${getBorder('1', 'outlineVariant', '0250')}}
    text-transform: uppercase;
    overflow: hidden;
    transition: all 300ms ease;
    
    :hover {
        background-image: ${getStateLayerColor('onSurface', 'hover')};
    }
  
    :active {
      border-radius: 25px;
    }
  }
`;

export const CopyWallet: FC<{ wallet: Address; fullWidth?: boolean }> = ({
  wallet,
  fullWidth,
}) => {
  const t = useTranslations();
  const [, copy] = useCopyToClipboard();

  const handleCopyWalletAddress = useCallback(() => {
    showSnackbar(t('WALLET_COPY_SUCCESS'));
    copy(wallet);
  }, [wallet]);

  return (
    <Container fullWidth={fullWidth}>
      <WalletAddress onClick={handleCopyWalletAddress}>
        {sliceWalletAddress(wallet)}
      </WalletAddress>
      <Copy onClick={handleCopyWalletAddress}>
        {t('SETTINGS_PAGE_ACTION_COPY')}
      </Copy>
    </Container>
  );
};
